package com.dotdrives.web.pages.drives

import com.dotdrives.controller.DriveDetailController
import com.dotdrives.controller.DriveDetailUiState
import com.dotdrives.controller.DriveFilters
import com.dotdrives.controller.DrivesBoardController
import com.dotdrives.controller.DrivesBoardUiState
import com.dotdrives.controller.DrivesView
import com.dotdrives.controller.NewDriveController
import com.dotdrives.controller.NewDriveUiState
import com.dotdrives.model.manualGiftFields
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.model.GiverSummary
import kotlinx.browser.window
import com.dotdrives.model.DrivePhase
import com.dotdrives.model.DriveSummary
import com.dotdrives.model.OrgMember
import com.dotdrives.web.ui.DotButton
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.b
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import react.useEffect
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text

external interface DrivesPageProps : Props {
    var giftController: com.dotdrives.controller.WorkflowController
    var giftState: com.dotdrives.controller.WorkflowState
    var moveController: com.dotdrives.controller.WorkflowController
    var moveWorkflowState: com.dotdrives.controller.WorkflowState
    var reminderController: com.dotdrives.controller.NewReminderController
    var reminderState: com.dotdrives.controller.NewReminderUiState
    var currentUserName: String
    var quickRecordController: com.dotdrives.controller.QuickRecordController
    var quickRecordState: com.dotdrives.controller.QuickRecordUiState
    var orgId: String
    var initialDriveId: String?
    var initialNewGiverId: String?
    var initialNewGiver: GiverSummary?
    var initialTemplateId: String?
    var initialPhaseId: String?
    var openNew: Boolean
    var boardController: DrivesBoardController
    var boardState: DrivesBoardUiState
    var detailController: DriveDetailController
    var detailState: DriveDetailUiState
    var newDriveController: NewDriveController
    var newDriveState: NewDriveUiState
    var members: List<OrgMember>
    var currentUserId: String
    var canWrite: Boolean
}

private data class PendingMove(
    val driveId: String,
    val driveTitle: String,
    val currentPhaseId: String,
    val currentPhaseName: String,
    val targetPhase: DrivePhase,
    val askAmount: Double?,
)

val DrivesPage = FC<DrivesPageProps> { props ->
    val board = props.boardState
    val (reminderOpen, setReminderOpen) = useState(false)
    val (recordOpen, setRecordOpen) = useState(false)
    val (receivedMove, setReceivedMove) = useState<PendingMove?>(null)
    val (selectedDriveId, setSelectedDriveId) = useState<String?>(null)
    val newDriveOpen = props.canWrite && (props.openNew || props.initialNewGiverId != null)
    val (filterOpen, setFilterOpen) = useState(false)
    val (pendingMove, setPendingMove) = useState<PendingMove?>(null)

    useEffect(props.reminderState.savedReminder?.id) {
        if (props.reminderState.savedReminder != null) setReminderOpen(false)
    }
    useEffect(props.initialDriveId) {
        setReminderOpen(false)
        setRecordOpen(false)
        setSelectedDriveId(props.initialDriveId)
        props.initialDriveId?.let(props.detailController::open)
    }
    useEffect(props.initialNewGiverId, props.openNew, props.canWrite, props.initialTemplateId, props.initialPhaseId) {
        // A history transition owns the draft lifetime, including removal of the intent.
        props.newDriveController.start(
            templateId = if (newDriveOpen) props.initialTemplateId ?: board.selectedTemplateId else null,
            initialPhaseId = if (newDriveOpen) props.initialPhaseId else null,
        )
        if (newDriveOpen) {
            props.initialNewGiver?.takeIf { it.id == props.initialNewGiverId }
                ?.let(props.newDriveController::selectGiver)
        }
    }
    useEffect(props.initialNewGiver?.id, props.initialNewGiverId) {
        if (newDriveOpen) {
            props.initialNewGiver?.takeIf { it.id == props.initialNewGiverId }
                ?.let(props.newDriveController::selectGiver)
        }
    }
    useEffect(board.selectedTemplateId) {
        if (newDriveOpen && props.newDriveState.templateId == null) board.selectedTemplateId?.let(props.newDriveController::setTemplateId)
    }
    useEffect(props.boardController) {
        props.boardController.load()
    }

    useEffect(props.newDriveState.savedDrive?.id) {
        if (newDriveOpen && props.newDriveState.savedDrive != null) {
            val saved = requireNotNull(props.newDriveState.savedDrive)
            window.location.hash = com.dotdrives.web.entityRouteHash(com.dotdrives.web.AppRoute.Drives, saved.orgId, saved.id)
            props.boardController.load()
        }
    }

    fun openDrive(drive: DriveSummary) {
        props.boardController.setView(DrivesView.Active)
        window.location.hash = com.dotdrives.web.entityRouteHash(com.dotdrives.web.AppRoute.Drives, props.orgId, drive.id)
    }

    useEffect(props.moveWorkflowState.result) {
        props.moveWorkflowState.result?.get("drive_id")?.toString()?.trim('"')?.let { id ->
            props.boardController.load()
            if (selectedDriveId == id) props.detailController.open(id)
            setReceivedMove(null)
        }
    }

    useEffect(props.giftState.result) {
        if (props.giftState.result?.get("donation_id") != null) {
            setRecordOpen(false)
            props.boardController.load()
            selectedDriveId?.let(props.detailController::open)
        }
    }

    fun submitMove(move: PendingMove, outcome: String?, gift: com.dotdrives.model.NewDonation? = null) {
        val fields = mutableMapOf("drive_id" to move.driveId, "from_phase_id" to move.currentPhaseId, "to_phase_id" to move.targetPhase.id)
        outcome?.let { fields["outcome"] = it }
        if (outcome == "pledged") move.askAmount?.let { fields["amount"] = it.toString() }
        gift?.let {
            fields["gift_amount"] = it.amount.toString()
            fields["payment_type"] = it.paymentType
            fields["memo"] = it.memo
            fields["occurred_at"] = it.occurredAt.toString()
            it.targetId?.let { target -> fields["target_id"] = target }
        }
        props.moveController.perform("workflows", "drive.move", fields)
    }

    fun requestMove(
        driveId: String,
        currentPhaseId: String,
        currentPhaseName: String,
        driveTitle: String,
        target: DrivePhase,
        askAmount: Double?,
    ) {
        if (isTerminalPhase(target)) {
            setPendingMove(PendingMove(driveId, driveTitle, currentPhaseId, currentPhaseName, target, askAmount))
        } else {
            submitMove(PendingMove(driveId, driveTitle, currentPhaseId, currentPhaseName, target, askAmount), null)
        }
    }

    fun confirmMove(outcome: String?) {
        val move = pendingMove ?: return
        if (outcome == "received") {
            val drive = board.drives.firstOrNull { it.id == move.driveId }
            val detailDrive = props.detailState.drive?.takeIf { it.id == move.driveId }
            if (drive != null) {
                props.quickRecordController.startForDrive(drive)
                setReceivedMove(move)
            } else if (detailDrive != null) {
                props.quickRecordController.startForDrive(detailDrive)
                setReceivedMove(move)
            }
        } else submitMove(move, outcome)
        setPendingMove(null)
    }

    DrivesStyles()
    com.dotdrives.web.ui.WorkflowFeedback { controller = props.giftController; state = props.giftState }
    com.dotdrives.web.ui.WorkflowFeedback { controller = props.moveController; state = props.moveWorkflowState }
    if (receivedMove != null) {
        com.dotdrives.web.pages.donations.DonationsStyles()
        com.dotdrives.web.pages.donations.QuickRecordDialog {
            controller = props.quickRecordController
            completesDriveMove = true
            state = props.quickRecordState
            targets = board.targets
            externalError = props.moveWorkflowState.error
            onRetryWorkflow = if (props.moveWorkflowState.pending != null) props.moveController::retry else null
            submitting = props.moveWorkflowState.busy || props.moveWorkflowState.pending != null
            onCancel = { if (!props.moveWorkflowState.busy) setReceivedMove(null) }
            onSaved = {}
            onSubmit = {
                props.quickRecordController.validatedDonation()?.let { gift ->
                    receivedMove?.let { submitMove(it, "received", gift) }
                }
            }
        }
    }

    div {
        className = ClassName("drives-page")

        div {
            className = ClassName("drives-header")
            div {
                className = ClassName("drives-header-row")
                div {
                    className = ClassName("drives-title-row")
                    div {
                        className = ClassName("drives-title")
                        +"Drives"
                        span {
                            className = ClassName("drives-count")
                            +board.totalCount.toString()
                        }
                    }
                    select {
                        className = ClassName("drives-template-select")
                        ariaLabel = "Drive process"
                        value = board.selectedTemplateId ?: ""
                        disabled = board.templates.isEmpty()
                        onChange = { props.boardController.selectTemplate(it.currentTarget.value) }
                        if (board.templates.isEmpty()) option {
                            value = ""
                            +if (board.loading) "Loading processes…" else "No processes"
                        }
                        board.templates.forEach { template ->
                            option { key = template.id.unsafeCast<Key>(); value = template.id; +template.name }
                        }
                    }
                    div {
                        className = ClassName("drives-view-toggle")
                        button {
                            type = ButtonType.buttonType
                            className = ClassName("drives-view-toggle-btn")
                            onClick = { props.boardController.setView(DrivesView.Completed) }
                            +"Closed history"
                        }
                    }
                }
                div {
                    className = ClassName("drives-topbar-actions")
                    DotButton {
                        text = if (board.exporting) "Exporting…" else "⇩ Export CSV"
                        variant = DotButtonVariant.Secondary
                        className = "drives-topbar-button"
                        onClick = { props.boardController.exportAll(::exportDrivesCsv) }
                        enabled = board.totalCount > 0 && !board.exporting
                    }
                    if (props.canWrite) {
                        DotButton {
                            text = "+ New drive"
                            className = "drives-topbar-button"
                            onClick = {
                                window.location.hash = com.dotdrives.web.workspaceHash("#/drives?new=1", props.orgId)
                            }
                        }
                    }
                }
            }

            run {
                div {
                    className = ClassName("drives-search-row")
                    input {
                        className = ClassName("drives-search")
                        type = InputType.text
                        value = board.query
                        placeholder = "⌕ Search drives…"
                        ariaLabel = "Search drives"
                        onChange = { props.boardController.search(it.currentTarget.value) }
                    }
                    div {
                        className = ClassName("drives-filter-popover-wrap")
                        button {
                            type = ButtonType.buttonType
                            className = ClassName(
                                "drives-filter-button" + if (hasActiveFilters(board.filters)) " drives-filter-button-active" else "",
                            )
                            onClick = { setFilterOpen(!filterOpen) }
                            +activeFilterLabel(board.filters)
                        }
                        if (filterOpen) {
                            FilterPopover {
                                filters = board.filters
                                members = props.members
                                targets = board.targets
                                availableTags = board.availableTags
                                matchCount = board.totalCount.toInt()
                                onChange = { props.boardController.setFilter(it) }
                                onClose = { setFilterOpen(false) }
                            }
                        }
                    }
                    board.financialTotals?.let { totals ->
                        div {
                            className = ClassName("drives-financials-row")
                            span { +"PROPOSED "; b { +formatMoneyCompact(totals.proposedAmount) } }
                            span { +"PLEDGED "; b { +formatMoneyCompact(totals.pledgedAmount) } }
                            span { +"RECEIVED "; b { +formatMoneyCompact(totals.receivedAmount) } }
                        }
                    }
                }
            }
        }

        run {
            DriveBoard {
                loading = board.loading
                phases = board.phases
                drives = board.drives
                phaseStats = board.phaseStats
                loadingPhases = board.loadingPhases
                onLoadMore = props.boardController::loadMore
                query = board.query
                hasActiveFilters = hasActiveFilters(board.filters)
                error = board.configurationError ?: board.activeError
                canWrite = props.canWrite
                onRetry = props.boardController::retry
                onOpen = ::openDrive
                onRequestMove = { drive, target ->
                    val currentName = board.phases.firstOrNull { it.id == drive.currentPhaseId }?.name ?: ""
                    requestMove(drive.id, drive.currentPhaseId, currentName, drive.title, target, drive.askAmount)
                }
                onNewDrive = { phase ->
                    window.location.hash = com.dotdrives.web.newDrivePhaseRouteHash(props.orgId, phase.templateId, phase.id)
                }
                onClearFilters = {
                    props.boardController.search("")
                    props.boardController.setFilter(DriveFilters())
                }
            }
        }
    }

    if (board.view == DrivesView.Completed && selectedDriveId == null && !newDriveOpen) {
        ClosedHistoryDrawer {
            state = board
            controller = props.boardController
            onOpen = ::openDrive
            onClose = { props.boardController.setView(DrivesView.Active) }
        }
    }

    if (selectedDriveId != null && !newDriveOpen) {
        DriveDetailPanel {
            giftController = props.giftController
            giftState = props.giftState
            moveController = props.moveController
            moveState = props.moveWorkflowState
            modalBlocked = reminderOpen || recordOpen || receivedMove != null || pendingMove != null
            onReminder = {
                props.detailState.drive?.let { drive ->
                    props.reminderController.start()
                    props.reminderController.selectSubject(com.dotdrives.model.ReminderSubjectOption("drive", drive.id, drive.title, drive.giverDisplayName))
                    setReminderOpen(true)
                }
            }
            onRecordGift = {
                props.detailState.drive?.let { drive ->
                    props.quickRecordController.startForDrive(drive)
                    setRecordOpen(true)
                }
            }
            state = props.detailState
            controller = props.detailController
            phases = board.phases
            canWrite = props.canWrite
            onClose = { setSelectedDriveId(null); window.location.hash = com.dotdrives.web.workspaceHash("#/drives", props.orgId) }
            onMoveToPhase = { target ->
                val drive = props.detailState.drive
                if (drive != null) {
                    requestMove(
                        drive.id,
                        drive.currentPhaseId,
                        drive.currentPhaseName ?: "",
                        drive.title,
                        target,
                        drive.askAmount,
                    )
                }
            }
        }
    }

    if (reminderOpen) {
        com.dotdrives.web.pages.nextsteps.NextStepsStyles()
        com.dotdrives.web.pages.nextsteps.NewReminderDialog {
            controller = props.reminderController; state = props.reminderState; members = props.members
            currentUserId = props.currentUserId; currentUserName = props.currentUserName; now = kotlin.time.Clock.System.now()
            onCancel = { setReminderOpen(false) }
        }
    }
    if (recordOpen) {
        com.dotdrives.web.pages.donations.DonationsStyles()
        com.dotdrives.web.pages.donations.QuickRecordDialog {
            controller = props.quickRecordController; state = props.quickRecordState; targets = board.targets
            externalError = props.giftState.error
            onRetryWorkflow = if (props.giftState.pending != null) props.giftController::retry else null
            submitting = props.giftState.busy || props.giftState.pending != null
            onSubmit = {
                props.quickRecordController.validatedDonation()?.let {
                    props.giftController.perform("workflows", "manual.gift", it.manualGiftFields())
                }
            }
            onCancel = { setRecordOpen(false) }
            onSaved = {
                setRecordOpen(false)
                props.boardController.load()
                selectedDriveId?.let(props.detailController::open)
            }
        }
    }

    if (newDriveOpen) {
        NewDriveDialog {
            controller = props.newDriveController
            state = props.newDriveState
            templates = board.templates
            targets = board.targets
            members = props.members
            onCancel = { window.location.hash = com.dotdrives.web.workspaceHash("#/drives", props.orgId) }
        }
    }

    pendingMove?.let { move ->
        MoveConfirmDialog {
            driveTitle = move.driveTitle
            targetPhaseName = move.targetPhase.name
            askAmount = move.askAmount
            onCancel = { setPendingMove(null) }
            onConfirm = ::confirmMove
        }
    }
}

private fun hasActiveFilters(filters: DriveFilters): Boolean =
    filters.stewardUserId != null || filters.targetId != null || filters.tag != null || filters.giverType != null

private fun activeFilterLabel(filters: DriveFilters): String {
    val count = listOfNotNull(filters.stewardUserId, filters.targetId, filters.tag, filters.giverType).size
    return if (count == 0) "Filters" else "Filters · $count"
}

private fun exportDrivesCsv(drives: List<DriveSummary>, phases: List<DrivePhase>) {
    val phaseNames = phases.associateBy(DrivePhase::id)
    val header = "Title,Giver,Phase,Target,Ask Amount,Steward,Tags"
    val rows = drives.joinToString("\r\n") { drive ->
        listOf(
            drive.title,
            drive.giverDisplayName,
            phaseNames[drive.currentPhaseId]?.name.orEmpty(),
            drive.target.orEmpty(),
            drive.askAmount?.toString().orEmpty(),
            drive.stewardName.orEmpty(),
            drive.tags.joinToString(";"),
        ).joinToString(",") { field -> csvEscape(field) }
    }
    val csv = "$header\r\n$rows"
    downloadCsv(csv, "dot-drives-drives.csv")
}

private fun csvEscape(value: String): String =
    if (value.contains(',') || value.contains('"') || value.contains('\n')) {
        "\"${value.replace("\"", "\"\"")}\""
    } else {
        value
    }

private fun downloadCsv(csv: String, fileName: String) {
    val holder = js("({})")
    holder.csv = csv
    holder.fileName = fileName
    kotlinx.browser.window.asDynamic().__dotDrivesCsvExport = holder
    js(
        """{
          const h = window.__dotDrivesCsvExport;
          const blob = new Blob([h.csv], { type: 'text/csv;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = h.fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
          delete window.__dotDrivesCsvExport;
        }""",
    )
}
